import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import {
  ECOM_PAYMENT_METHODS_DEFAULT,
  makeEcomPaymentMethod,
  type EcomPaymentMethod,
  type ShippingCourier,
} from '@mlm/shared';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@mlm/ui';
import {
  ecomPaymentMethodLogoUrl,
  ecomPaymentMethodQrUrl,
  getShippingConfig,
  getTenantFeatures,
  patchTenantFeatures,
  uploadEcomPaymentMethodLogo,
  uploadEcomPaymentMethodQr,
} from '../lib/api';
import { hasPerm } from '../lib/auth-store';
import { ConfirmSaveButton } from '../components/confirm-save-button';

/**
 * Ecom payment methods — admin-managed registry for member checkout. Each row
 * pairs a `currency` (cash/gc/coin) with a `settlement` (wallet → instant slot-
 * wallet debit; manual → order at `pending`, member uploads proof from the
 * order detail page). Saving needs the grantable `tenant.payment_methods.write`
 * (super_admin passes implicitly; the backend enforces the same key-scoped
 * carve-out). Mirrors the typed-editor pattern in `payout-deductions.tsx`.
 */
function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

export function EcomPaymentMethodsPage() {
  const qc = useQueryClient();
  const canWrite = hasPerm('tenant.payment_methods.write');
  const q = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });
  // COD-eligible couriers from the active shipping config — the only valid
  // choices for a cod_manual method's default courier.
  const shippingQ = useQuery({ queryKey: ['admin-shipping-config'], queryFn: getShippingConfig });
  const codCouriers = (shippingQ.data?.couriers ?? []).filter((c) => c.enabled && c.cod === true);

  const [methods, setMethods] = useState<EcomPaymentMethod[]>([]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!q.data) return;
    const cfg = q.data.ecom_payment_methods ?? ECOM_PAYMENT_METHODS_DEFAULT;
    setMethods(Array.isArray(cfg.methods) ? clone(cfg.methods) : []);
  }, [q.data]);

  const save = useMutation({
    meta: { successMessage: 'Payment methods saved.' },
    mutationFn: () => patchTenantFeatures({ ecom_payment_methods: { methods } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant-features'] }),
  });

  function updateMethod(i: number, patch: Partial<EcomPaymentMethod>) {
    setMethods((ms) => ms.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));
  }
  function addMethod() {
    setMethods((ms) => [...ms, makeEcomPaymentMethod()]);
  }
  function removeMethod(i: number) {
    setMethods((ms) => ms.filter((_, idx) => idx !== i));
  }

  // Validate + surface the error inline; returns the message (or null) so the
  // confirm dialog only opens for valid input.
  function validate(): string | null {
    const codes = methods.map((m) => m.code.trim());
    let msg: string | null = null;
    if (codes.some((c) => c === '')) msg = 'Every method needs a code.';
    else if (codes.some((c) => !/^[a-z0-9_]+$/.test(c)))
      msg = 'Codes must be lowercase letters, digits, or underscores.';
    else if (new Set(codes).size !== codes.length) msg = 'Method codes must be unique.';
    else if (methods.some((m) => m.label.trim() === '')) msg = 'Every method needs a label.';
    // GC/coin can't be settled by manual proof upload — there's nothing off-ledger
    // to prove. Enforce here so admins don't shoot their members in the foot.
    else if (methods.some((m) => m.currency !== 'cash' && m.settlement === 'manual'))
      msg = 'Manual payment is only valid for the cash currency.';
    // Online gateways (Dragonpay/iPay88) transact PHP cash only — gc/coin can't
    // route to a gateway.
    else if (methods.some((m) => m.currency !== 'cash' && m.settlement === 'gateway'))
      msg = 'Online gateway payment is only valid for the cash currency.';
    else if (methods.some((m) => m.settlement === 'gateway' && !m.gatewayProvider))
      msg = 'Pick a gateway provider for each online-gateway method.';
    // Cash on Delivery (Manual) collects cash off-platform on delivery — gc/coin
    // wallets have no COD counterpart.
    else if (methods.some((m) => m.currency !== 'cash' && m.settlement === 'cod_manual'))
      msg = 'Cash on Delivery is only valid for the cash currency.';
    // CODM needs a default courier (an enabled, COD-eligible one) to price
    // shipping at checkout — the buyer never picks a courier for COD.
    else if (
      methods.some(
        (m) =>
          m.settlement === 'cod_manual' &&
          (!m.defaultCourierId || !codCouriers.some((c) => c.id === m.defaultCourierId)),
      )
    )
      msg = 'Each Cash on Delivery method needs a valid COD-eligible default courier.';
    setErr(msg);
    return msg;
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Ecom payment methods</h1>
        <p className="text-sm text-muted-foreground">
          The methods members can pick at checkout. Wallet methods debit the buyer's slot wallet and
          complete the order instantly. Manual methods land the order at{' '}
          <span className="font-mono">pending</span>: the member uploads proof of payment from the
          order detail page, and admin approves it from the manual-payments queue.
        </p>
        {!canWrite && (
          <p className="text-xs text-muted-foreground mt-1">
            Read-only. Saving needs the &ldquo;Edit payment methods &amp; POS fees&rdquo;
            permission (<span className="font-mono">tenant.payment_methods.write</span>).
          </p>
        )}
      </div>

      {q.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          {methods.length === 0 && (
            <Card>
              <CardContent className="py-6 text-sm text-muted-foreground">
                No payment methods yet.{' '}
                {canWrite
                  ? 'Add one below.'
                  : 'An admin with the payment-methods edit permission can add them.'}{' '}
                Members can't check out until at least one method is enabled.
              </CardContent>
            </Card>
          )}

          {methods.map((m, i) => (
            <MethodCard
              key={i}
              method={m}
              canWrite={canWrite}
              codCouriers={codCouriers}
              onChange={(patch) => updateMethod(i, patch)}
              onRemove={() => removeMethod(i)}
            />
          ))}

          {canWrite && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={addMethod}>
                <Plus className="h-4 w-4" /> Add method
              </Button>
              <ConfirmSaveButton
                size="sm"
                label="Save"
                title="Save payment methods"
                current={q.data?.ecom_payment_methods}
                next={{ methods }}
                loading={save.isPending}
                validate={validate}
                onInvalid={setErr}
                onConfirm={() => save.mutateAsync()}
              />
              {save.isSuccess && !save.isPending && (
                <span className="text-xs text-success">Saved.</span>
              )}
              {err && <span className="text-xs text-destructive">{err}</span>}
              {save.error && (
                <span className="text-xs text-destructive">Save failed. Try again.</span>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function MethodCard({
  method: m,
  canWrite,
  codCouriers,
  onChange,
  onRemove,
}: {
  method: EcomPaymentMethod;
  canWrite: boolean;
  codCouriers: ShippingCourier[];
  onChange: (patch: Partial<EcomPaymentMethod>) => void;
  onRemove: () => void;
}) {
  // Local object-URL preview for a just-picked logo (the public read endpoint
  // only resolves once the config is saved), falling back to the saved logo.
  const [localLogo, setLocalLogo] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoSrc = localLogo ?? (m.logoKey && m.code ? ecomPaymentMethodLogoUrl(m.code) : null);

  async function onLogoPick(file: File) {
    setLocalLogo(URL.createObjectURL(file));
    setUploadingLogo(true);
    try {
      const { key } = await uploadEcomPaymentMethodLogo(file);
      onChange({ logoKey: key });
    } finally {
      setUploadingLogo(false);
    }
  }

  // Scannable payment QR (manual methods only) — same upload mechanics as logo.
  const [localQr, setLocalQr] = useState<string | null>(null);
  const [uploadingQr, setUploadingQr] = useState(false);
  const qrSrc = localQr ?? (m.qrKey && m.code ? ecomPaymentMethodQrUrl(m.code) : null);

  async function onQrPick(file: File) {
    setLocalQr(URL.createObjectURL(file));
    setUploadingQr(true);
    try {
      const { key } = await uploadEcomPaymentMethodQr(file);
      onChange({ qrKey: key });
    } finally {
      setUploadingQr(false);
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Display label">
            <Input
              className="h-8"
              value={m.label}
              disabled={!canWrite}
              placeholder="Bank Transfer"
              onChange={(e) => onChange({ label: e.target.value })}
            />
          </Field>
          <Field label="Code (stored on the order)">
            <Input
              className="h-8 font-mono"
              value={m.code}
              disabled={!canWrite}
              placeholder="bank_transfer"
              onChange={(e) =>
                onChange({ code: e.target.value.toLowerCase().replace(/\s+/g, '_') })
              }
            />
          </Field>
          <Field label="Currency">
            <Select
              value={m.currency}
              disabled={!canWrite}
              onValueChange={(v) => onChange({ currency: v as EcomPaymentMethod['currency'] })}
            >
              <SelectTrigger className="h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="gc">GC</SelectItem>
                <SelectItem value="coin">Coin</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Settlement">
            <Select
              value={m.settlement}
              disabled={!canWrite}
              onValueChange={(v) => {
                const settlement = v as EcomPaymentMethod['settlement'];
                onChange({
                  settlement,
                  // Default the provider so the gateway sub-fields + validator have a value.
                  ...(settlement === 'gateway' && !m.gatewayProvider
                    ? { gatewayProvider: 'dragonpay' as const }
                    : {}),
                });
              }}
            >
              <SelectTrigger className="h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="wallet">Wallet (instant)</SelectItem>
                <SelectItem value="manual">Manual (proof upload)</SelectItem>
                <SelectItem value="gateway">Online gateway (Dragonpay/iPay88)</SelectItem>
                <SelectItem value="cod_manual">
                  Cash on Delivery — Manual (admin-controlled)
                </SelectItem>
              </SelectContent>
            </Select>
          </Field>
          {m.settlement === 'gateway' && (
            <>
              <Field label="Gateway provider">
                <Select
                  value={m.gatewayProvider ?? 'dragonpay'}
                  disabled={!canWrite}
                  onValueChange={(v) =>
                    onChange({ gatewayProvider: v as EcomPaymentMethod['gatewayProvider'] })
                  }
                >
                  <SelectTrigger className="h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dragonpay">Dragonpay</SelectItem>
                    <SelectItem value="ipay88">iPay88</SelectItem>
                    <SelectItem value="stub">Stub (testing)</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Default channel / PaymentId (optional)">
                <Input
                  className="h-8"
                  value={m.gatewayPaymentId ?? ''}
                  disabled={!canWrite}
                  placeholder="blank = let the buyer choose at checkout"
                  onChange={(e) => onChange({ gatewayPaymentId: e.target.value })}
                />
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {m.gatewayProvider === 'dragonpay' ? (
                    <>
                      <strong>Leave blank (recommended)</strong> — the buyer picks the channel
                      (GCash, card, …) at checkout from the merchant's enabled list. Set a fixed
                      ProcId (e.g. <code>GCSH</code>) only to force a single channel and skip the
                      picker.
                    </>
                  ) : (
                    <>Leave blank to let the customer pick the channel on the gateway's page.</>
                  )}{' '}
                  Online gateways transact cash (PHP) only; the customer is redirected to pay and a
                  verified webhook confirms the order — no proof upload or admin approval.
                  Credentials live in the server <code>.env</code>, never here.
                </p>
              </Field>
            </>
          )}
          <Field label="Logo (shown beside the method at checkout)">
            <div className="flex items-center gap-3">
              {logoSrc ? (
                <img
                  src={logoSrc}
                  alt=""
                  className="h-10 w-10 rounded border object-contain bg-white"
                />
              ) : (
                <div className="h-10 w-10 rounded border bg-muted" />
              )}
              <input
                type="file"
                accept="image/*"
                className="text-xs"
                disabled={!canWrite || uploadingLogo}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void onLogoPick(f);
                }}
              />
              {uploadingLogo && <span className="text-xs text-muted-foreground">Uploading…</span>}
              {m.logoKey && !uploadingLogo && canWrite && (
                <button
                  type="button"
                  className="text-xs text-muted-foreground hover:text-destructive"
                  onClick={() => {
                    setLocalLogo(null);
                    onChange({ logoKey: undefined });
                  }}
                >
                  Remove
                </button>
              )}
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Save to apply. Recommended: a small square PNG.
            </p>
          </Field>
          {m.settlement === 'manual' && (
            <>
              <Field label="Account label (shown to member)">
                <Input
                  className="h-8"
                  value={m.accountLabel ?? ''}
                  disabled={!canWrite}
                  placeholder="Bank · Account · Holder"
                  onChange={(e) => onChange({ accountLabel: e.target.value })}
                />
              </Field>
              <Field label="Instructions (Markdown, shown prominently on the order detail)">
                <Textarea
                  rows={5}
                  value={m.instructions ?? ''}
                  disabled={!canWrite}
                  placeholder={
                    '# Step 1\n1. Open the app\n2. Send to the account above\n3. **Upload your receipt below**'
                  }
                  onChange={(e) => onChange({ instructions: e.target.value })}
                  className="font-mono text-xs"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Markdown supported: use <code>#</code> headings, <code>1.</code> numbered lists,
                  <code> **bold** </code>, and links. Rendered as a prominent &quot;How to pay&quot;
                  card on the order detail page after the order is placed.
                </p>
              </Field>
              <Field label="Payment QR (scannable by the customer at checkout)">
                <div className="flex items-center gap-3">
                  {qrSrc ? (
                    <img
                      src={qrSrc}
                      alt=""
                      className="h-20 w-20 rounded border object-contain bg-white"
                    />
                  ) : (
                    <div className="h-20 w-20 rounded border bg-muted" />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    className="text-xs"
                    disabled={!canWrite || uploadingQr}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void onQrPick(f);
                    }}
                  />
                  {uploadingQr && <span className="text-xs text-muted-foreground">Uploading…</span>}
                  {m.qrKey && !uploadingQr && canWrite && (
                    <button
                      type="button"
                      className="text-xs text-muted-foreground hover:text-destructive"
                      onClick={() => {
                        setLocalQr(null);
                        onChange({ qrKey: undefined });
                      }}
                    >
                      Remove
                    </button>
                  )}
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Save to apply. Upload your GCash / Maya / bank QR; the customer scans it on the
                  order&apos;s pay page.
                </p>
              </Field>
            </>
          )}
          {m.settlement === 'cod_manual' && (
            <>
              <Field label="Default courier">
                {codCouriers.length === 0 ? (
                  <p className="text-[11px] text-muted-foreground">
                    No COD-eligible couriers yet. Enable a courier with COD in the Shipping editor
                    first, then pick it here.
                  </p>
                ) : (
                  <Select
                    value={m.defaultCourierId ?? ''}
                    disabled={!canWrite}
                    onValueChange={(v) => onChange({ defaultCourierId: v })}
                  >
                    <SelectTrigger className="h-8">
                      <SelectValue placeholder="Pick a COD-eligible courier" />
                    </SelectTrigger>
                    <SelectContent>
                      {codCouriers.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <p className="mt-1 text-[11px] text-muted-foreground">
                  COD orders skip the online-payment step where a courier is normally chosen, so the
                  total can't include shipping without one. This courier prices shipping at checkout
                  (the buyer may still switch to another COD-eligible courier).
                </p>
              </Field>
              <p className="text-[11px] text-muted-foreground">
                Cash on Delivery — orders placed with this method skip payment and land at
                <strong> Ready to Ship</strong>. No PV or wallet commissions are released until an
                admin marks the order <strong>Completed</strong> from the Orders page. You control
                the entire status flow.
              </p>
            </>
          )}
          <Field label="Enabled">
            <label className="inline-flex items-center gap-2 text-sm">
              <Checkbox
                checked={m.enabled}
                disabled={!canWrite}
                onCheckedChange={(v) => onChange({ enabled: v === true })}
              />
              <span className="text-muted-foreground">Visible at checkout</span>
            </label>
          </Field>
          <Field label="Virtual products">
            <label className="inline-flex items-center gap-2 text-sm">
              {/* Opt-OUT: an unset field means allowed, so an existing registry
                  keeps every method available and a method added later doesn't
                  silently vanish from the virtual funnel. */}
              <Checkbox
                checked={m.virtualEnabled !== false}
                disabled={!canWrite}
                onCheckedChange={(v) => onChange({ virtualEnabled: v === true })}
              />
              <span className="text-muted-foreground">
                Offer on courses / services (no delivery)
              </span>
            </label>
          </Field>
        </div>
        {m.virtualEnabled === false && (
          <p className="mt-1 text-xs text-muted-foreground">
            Hidden when the cart is entirely virtual. Cash on Delivery is the usual reason — there
            is nothing to hand over, so such an order could never be collected.
          </p>
        )}
        {canWrite && (
          <Button variant="ghost" size="sm" onClick={onRemove} aria-label="Remove method">
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </CardHeader>
      <CardContent />
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs text-muted-foreground">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
